import type { JsonSchemaType } from '@gmn-portal/backend';
import { useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useGlobalMetadata, useUpdateGlobalMetadata } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageHeading } from '@/shared/components/layout';
import {
  type FieldError,
  MetadataBuilder,
  MetadataFormPreview,
  parseMetadataApiErrors,
} from '@/shared/components/metadata/metadata-builder';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui';

// Default empty schema
const defaultMetadataSchema: JsonSchemaType = {
  type: 'object',
  groups: [],
  properties: {},
  required: [],
  propertyOrder: [],
};

export function ManageContractMetadata() {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { data: globalMetadata, isLoading } = useGlobalMetadata();
  const updateGlobalMetadata = useUpdateGlobalMetadata();
  const [isUpdating, setIsUpdating] = useState(false);
  const [metadataSchema, setMetadataSchema] = useState<JsonSchemaType>(defaultMetadataSchema);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

  const isGmnAdmin = currentUser?.role === 'gmn-admin';

  // Load existing metadata when data is available
  useEffect(() => {
    if (globalMetadata && !isLoading) {
      setMetadataSchema(
        (globalMetadata.rootContractMetadata as JsonSchemaType) || defaultMetadataSchema
      );
    }
  }, [globalMetadata, isLoading]);

  const handleSubmit = async () => {
    if (!isGmnAdmin) return;

    setIsUpdating(true);
    setFieldErrors([]); // Clear previous errors

    try {
      await updateGlobalMetadata.mutateAsync({
        rootContractMetadata: metadataSchema,
      });

      await queryClient.invalidateQueries({ queryKey: ['globalMetadata'] });
      toast.success(t('metadata.contractMetadataUpdatedSuccessfully'));
    } catch (error) {
      // Parse field-specific errors
      const parsedErrors = parseMetadataApiErrors(error, (key) => t(key));
      setFieldErrors(parsedErrors);

      // Handle remaining errors with toasts
      const payloadError = error as {
        response?: {
          errors?: Array<{
            data?: {
              errors?: Array<{
                code: string;
                message: string;
              }>;
            };
            message?: string;
          }>;
        };
      };

      if (payloadError.response?.errors) {
        payloadError.response.errors.forEach((err) => {
          if (err.data?.errors) {
            err.data.errors.forEach((apiError) => {
              // Only show toast for errors that weren't mapped to fields
              if (
                ![
                  'NewRequiredFieldAdditionNotAllowed',
                  'ExistingFieldMadeRequiredWithMissingValues',
                  'FieldTypeChangeNotAllowed',
                ].includes(apiError.code)
              ) {
                toast.error(t('metadata.metadataUpdateFailed'), {
                  description: apiError.message,
                });
              }
            });
          } else if (err.message) {
            toast.error(t('errors.unknownError'), {
              description: err.message,
            });
          }
        });
      } else if (parsedErrors.length === 0) {
        toast.error(t('errors.unknownError'));
      }
    }

    setIsUpdating(false);
  };

  // Clear errors when schema changes
  const handleSchemaChange = (schema: JsonSchemaType) => {
    setMetadataSchema(schema);
    if (fieldErrors.length > 0) {
      setFieldErrors([]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeading>{t('metadata.contractMetadata')}</PageHeading>
        <Button onClick={handleSubmit} disabled={!isGmnAdmin || isUpdating}>
          <Save className="size-4 mr-2" />
          {isUpdating ? t('common.saving') : t('common.save')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('metadata.rootContractMetadata')}</CardTitle>
          <CardDescription>{t('metadata.rootContractMetadataDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 min-h-[500px]">
            <MetadataBuilder
              value={metadataSchema}
              onChange={handleSchemaChange}
              disabled={!isGmnAdmin || isLoading}
              className="border-0 rounded-none shadow-none lg:border-r h-full"
              fieldErrors={fieldErrors}
              header={false}
            />
            <MetadataFormPreview
              schema={metadataSchema}
              className="border-0 rounded-none shadow-none hidden lg:flex"
              header={false}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
